import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthModal({ open, onClose, onSuccess, onGoogle }) {
  const { loginEmail, registerEmail } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setError(""); setBusy(true);
    try {
      if (mode === "register") await registerEmail(email, password, name);
      else await loginEmail(email, password);
      onSuccess();
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="auth-modal" className="max-w-sm bg-[#fbf6ec] grain">
        <DialogHeader>
          <div className="flex items-center justify-center gap-2 mb-1">
            <span className="thumbtack !relative !top-0 !left-0 !translate-x-0" />
            <span className="font-display font-black text-2xl tracking-tight text-[#2b2620]">Pin a Note</span>
          </div>
          <DialogTitle className="font-display font-black text-xl text-[#2b2620] text-center">{mode === "register" ? "Create your board" : "Welcome back"}</DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 bg-[#efe6d4] rounded-full p-1">
          <button data-testid="auth-tab-login" onClick={() => setMode("login")} className={`flex-1 rounded-full py-1.5 text-sm font-display font-bold ${mode === "login" ? "bg-[#2b2620] text-[#fdf6e3]" : "text-[#6b6155]"}`}>Log in</button>
          <button data-testid="auth-tab-register" onClick={() => setMode("register")} className={`flex-1 rounded-full py-1.5 text-sm font-display font-bold ${mode === "register" ? "bg-[#2b2620] text-[#fdf6e3]" : "text-[#6b6155]"}`}>Sign up</button>
        </div>

        <div className="space-y-3 mt-2">
          {mode === "register" && <Input data-testid="auth-name" placeholder="Display name" value={name} onChange={(e) => setName(e.target.value)} className="bg-white" />}
          <Input data-testid="auth-email" type="email" placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-white" />
          <Input data-testid="auth-password" type="password" placeholder="Password (6+ characters)" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} className="bg-white" />
          {error && <p data-testid="auth-error" className="text-sm text-[#ff3b30] font-medium">{error}</p>}
          <Button data-testid="auth-submit" onClick={submit} disabled={busy} className="w-full rounded-full bg-[#ff3b30] hover:bg-[#e0271d] text-white font-display font-bold">
            {busy ? "Please wait…" : mode === "register" ? "Create account" : "Log in"}
          </Button>
        </div>

        <div className="flex items-center gap-2 my-1"><span className="flex-1 h-px bg-[#e0d5bf]" /><span className="text-xs text-[#a89a86]">or</span><span className="flex-1 h-px bg-[#e0d5bf]" /></div>
        <Button data-testid="auth-google" onClick={onGoogle} variant="outline" className="w-full rounded-full bg-white hover:bg-[#f3ecdf] border-[#e0d5bf] text-[#2b2620] font-display font-bold">
          Continue with Google
        </Button>
      </DialogContent>
    </Dialog>
  );
}
